/** @jsxImportSource @emotion/react */
import { FormProvider, useController, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import * as authSelectors from "../../auth/authSelectors";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
import { DrawerSubHeader } from "../../../components/DrawerSubHeader";
import { getErrorMessage } from "../../../error-handler";
import { SwitchInput } from "../../../components/forms/inputs/SwitchInput";
import {
  PaymentProviderConfiguration,
  PaymentProviderConfigurationFormValues,
  PaymentProviderType,
} from "../models/payment-provider-configuration";
import {
  useCreatePaymentProviderConfiguration,
  useGetPaymentProviderConfiguration,
  useGetPaymentProviders,
  useUpdatePaymentProviderConfiguration,
} from "../service/payment-provider-configrations-api";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { SelectCountryInput } from "../../../components/forms/inputs/SelectCountryInput";
import { getCountrySpecificValues } from "../../../util";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";
import { SpinnerBlock } from "../../common/Spinner";
import { useGetAdyenStoresForOrganization } from "../../shop/service/shop-api";
import { SelectField } from "../../../components/forms/fields/SelectField";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  existingPaymentProviders: string[];
};

const additionalFormStyles = css`
  gap: 16px;
  align-items: start;
`;

const defaultPaymentMethodTypesOptions = [
  {
    label: "Recurring credit card",
    value: "card",
  },
  {
    label: "One time credit card",
    value: "card_new",
  },
  {
    label: "Apple Pay",
    value: "applepay",
  },
  {
    label: "Google Pay",
    value: "googlepay",
  },
  {
    label: "Vipps",
    value: "vipps_mobile_pay",
  },
  {
    label: "Mobile Pay",
    value: "mobilepay",
  },
];

const validationSchema = yup.object({
  provider: yup.string().required("Select provider to continue"),
  apiKey: yup.string(),
  clientId: yup.string(),
  countryCode: yup.string().required("Select country to continue"),
  defaultPaymentPoint: yup.string().when("provider", {
    is: (value: string) => {
      return value === "adyen" || value === "adyen_custom";
    },
    then: (schema) => schema.required("Default payment point is required"),
    otherwise: (schema) => schema.optional(),
  }),
  defaultPaymentMethodTypes: yup
    .array(
      yup.string().oneOf(defaultPaymentMethodTypesOptions.map((x) => x.value))
    )
    .min(1, "At least one payment method type is required"),
});

export const MutatePaymentProviderConfigurationDrawer = ({
  isOpen,
  onClose,
  existingPaymentProviders,
}: Props) => {
  const { option } = useSearchParamsManipulation();
  const [extraSettings, setExtraSettings] = useState<
    PaymentProviderConfiguration["extraSettings"]
  >({});
  const { countryCode } = getCountrySpecificValues();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [isLoadingMutation, setIsLoadingMutation] = useState(false);
  const isEdit = !!option;

  const defaultValues: PaymentProviderConfigurationFormValues = {
    apiKey: "",
    clientId: "",
    enabled: true,
    testing: false,
    provider: "",
    countryCode: countryCode,
    defaultPaymentPoint: "",
    priority: "0",
    isDefault: false,
    defaultPaymentMethodTypes: [],
  };

  const methods = useForm<PaymentProviderConfigurationFormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    field: { onChange: controllerOnChange, value, ref, ...controllerRest },
  } = useController({
    control: methods.control,
    name: "defaultPaymentMethodTypes",
  });

  const isAdyen = ["adyen", "adyen_custom"].includes(methods.watch("provider"));

  const { data: paymentProviders } = useGetPaymentProviders(
    organizationKeyName
  );

  const { data: configuration, isLoading } = useGetPaymentProviderConfiguration(
    organizationKeyName,
    option || undefined
  );

  const { data: adyenStores } = useGetAdyenStoresForOrganization(
    organizationKeyName
  );

  const {
    mutateAsync: createPaymentProviderConfiguration,
  } = useCreatePaymentProviderConfiguration();

  const {
    mutateAsync: updatePaymentProviderConfiguration,
  } = useUpdatePaymentProviderConfiguration();

  const handleOnClose = () => {
    methods.reset(defaultValues);
    onClose();
  };

  const onSubmit = async (data: PaymentProviderConfigurationFormValues) => {
    setIsLoadingMutation(true);
    const { provider, priority, ...rest } = data;

    try {
      if (isEdit) {
        await updatePaymentProviderConfiguration({
          organizationKeyName,
          data: {
            ...rest,
            provider: provider as PaymentProviderType,
            id: configuration?.id!,
            priority: priority ? Number(priority) : 0,
            extraSettings,
          },
        });
      } else {
        await createPaymentProviderConfiguration({
          data: {
            ...rest,
            provider: provider as PaymentProviderType,
            priority: priority ? Number(priority) : 0,
            extraSettings,
          },
          organizationKeyName,
        });
      }
      toastr.success(
        "Success",
        `Payment provider configuration successfully ${
          configuration?.id ? "updated" : "created"
        }`
      );
      handleOnClose();
      setIsLoadingMutation(false);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
      setIsLoadingMutation(false);
    }
  };

  useEffect(() => {
    if (!isEdit || !configuration) {
      return;
    }
    methods.reset({
      apiKey: configuration.apiKey,
      clientId: configuration.clientId,
      enabled: configuration.enabled,
      testing: configuration.testing,
      provider: configuration.provider,
      countryCode: configuration.countryCode,
      defaultPaymentPoint: configuration.defaultPaymentPoint || "",
      priority: configuration.priority.toString(),
      isDefault: configuration.isDefault,
      defaultPaymentMethodTypes: configuration.defaultPaymentMethodTypes || [],
    });
    // Empty object is parsed as array by PHP, so we need to check for that
    setExtraSettings(
      Array.isArray(configuration.extraSettings) || !configuration.extraSettings
        ? {}
        : configuration.extraSettings
    );
  }, [configuration, methods, isEdit]);

  return (
    <Drawer
      title={`${
        configuration?.id ? "Edit" : "Create"
      } payment provider configuration`}
      width={500}
      buttons={
        <ButtonAction
          disabled={isLoadingMutation}
          onClick={methods.handleSubmit(onSubmit)}
          type="button"
        >
          {isLoadingMutation ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      {isLoading ? (
        <SpinnerBlock />
      ) : (
        <StyledForm additionalStyles={additionalFormStyles}>
          <FormProvider {...methods}>
            <SelectInput
              label="Provider name"
              name="provider"
              isDisabled={!!configuration?.id}
              placeholder="Select provider"
              options={(paymentProviders || [])
                .filter((paymentProvider) => {
                  // For showing in disabled dropdown
                  const isChosenConfig =
                    isEdit && configuration?.provider === paymentProvider.value;

                  if (isChosenConfig) {
                    return true;
                  }

                  return !existingPaymentProviders.includes(
                    paymentProvider.value
                  );
                })
                .map((paymentProvider) => ({
                  label: paymentProvider.name,
                  value: paymentProvider.value,
                }))}
            />
            <SelectCountryInput name="countryCode" />
            {isAdyen && (
              <SelectInput
                label="Default payment point id"
                name="defaultPaymentPoint"
                helperText="Select between the Adyen stores for the organization"
                options={
                  adyenStores?.map((x) => ({
                    label: x.description,
                    value: x.reference || x.id,
                    isDisabled: !x.reference,
                  })) || []
                }
              />
            )}
            <TextInput
              label="API key"
              name="apiKey"
              placeholder="Enter API key"
            />
            <TextInput
              label="Client ID"
              name="clientId"
              placeholder="Enter client ID"
            />
            <TextInput
              label="Priority"
              name="priority"
              placeholder="Enter priority"
              type="number"
            />
            <SelectField
              isMulti
              closeMenuOnSelect={false}
              label="Default payment method types"
              placeholder="Start typing to search for default payment method types..."
              errorMessage={
                methods.getFieldState(
                  "defaultPaymentMethodTypes",
                  methods.formState
                ).error?.message
              }
              onChange={(value) => {
                const values = value as { value: string; label: string }[];
                controllerOnChange(values.map((x) => x.value));
              }}
              options={defaultPaymentMethodTypesOptions}
              value={(value || []).map((x) => ({
                value: x,
                label:
                  defaultPaymentMethodTypesOptions?.find((d) => d.value === x)
                    ?.label || "",
              }))}
              innerRef={ref}
              {...controllerRest}
            />

            <SwitchInput label="Is enabled?" name="enabled" />
            <SwitchInput label="Is for testing?" name="testing" />
            <SwitchInput
              label="Is default payment provider configuration?"
              name="isDefault"
            />
          </FormProvider>
          <DrawerSubHeader
            header="Extra settings"
            subtext="This is for extra non-standard settings. Contact HeapsGo before editing."
          />
          <JsonView
            src={extraSettings}
            onChange={(value) => {
              setExtraSettings(value.src as Record<string, any>);
            }}
            editable
            css={css`
              width: 100%;
            `}
          />
        </StyledForm>
      )}
    </Drawer>
  );
};
